/** Default and per-section SEO copy for KOB (slots, fishing, Shan Koe Mee, Bugyi, live casino, card games). */

/** Protocol + host (no trailing slash). Uses the live URL so one build works on KOB domains. */
export function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

/**
 * Absolute canonical URL for a route pathname (must match router SEO / index.html bootstrap).
 * @param {string} [path] pathname e.g. /home (query/hash ignored)
 */
export function getCanonicalUrlForPath(path) {
  const pathname = ((path || '/').split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const origin = getSiteOrigin();
  if (!origin) return '';
  return pathname === '/' ? origin : `${origin}${pathname}`;
}

const BASE_DESC =
  'KOB offers online slots, fishing arcade games, Shan Koe Mee (Shan poker), Bugyi and classic card games, plus live casino with real dealers. Play securely, fast deposits and withdrawals, promotions for Myanmar players.';

const BASE_KEYWORDS =
  'KOB, online slots, slot games, fishing game, fishing arcade, Shan Koe Mee, Shan poker, ဘူဂျီ, Bugyi, Bugyee, online card games, live casino, live dealer, baccarat, roulette, Myanmar online casino, jackpot slots';

export const siteDefaults = {
  title: 'KOB | Online Slots, Fishing, Shan Koe Mee, Bugyi, Live Casino & Card Games',
  description: BASE_DESC,
  keywords: BASE_KEYWORDS,
};

export const pageMeta = {
  home: siteDefaults,

  slots: {
    title: 'Online Slot Games | KOB — Jackpots & Popular Slots',
    description:
      'Play hundreds of online slot games at KOB: classic reels, video slots, jackpots and bonus features. Fast play, fair results, mobile-friendly.',
    keywords: `${BASE_KEYWORDS}, video slots, jackpot slots, slot bonus`,
  },

  fishing: {
    title: 'Fishing Arcade Games Online | KOB',
    description:
      'Shoot-and-catch fishing arcade games at KOB. Colorful tables, multipliers and real-time action—popular in Myanmar and Southeast Asia.',
    keywords: `${BASE_KEYWORDS}, fishing shooter game, arcade fishing`,
  },

  liveCasino: {
    title: 'Live Casino & Live Dealer Games | KOB',
    description:
      'Live casino at KOB: real dealers, HD streams, baccarat, roulette and more. Feel like a real casino floor from your phone or desktop.',
    keywords: `${BASE_KEYWORDS}, live baccarat, live roulette, HD casino stream`,
  },

  shanKoeMee: {
    title: 'Shan Koe Mee Online Rooms | KOB',
    description:
      'Join Shan Koe Mee (Shan poker) tables at KOB. Play with friends or match online—authentic card rules and smooth gameplay.',
    keywords: `${BASE_KEYWORDS}, Shan Koe Mee room, Shan poker online`,
  },

  bugyee: {
    title: 'Bugyi (Bugyee) Card Game Online | KOB',
    description:
      'Play Bugyi / Bugyee card games online at KOB. Traditional Myanmar-style card play with reliable tables and quick rounds.',
    keywords: `${BASE_KEYWORDS}, Bugyi online, Bugyee card game`,
  },

  cardGames: {
    title: 'Online Card Games & Tables | KOB',
    description:
      'Card games and table games at KOB alongside slots and live casino. One account for multiple game types.',
    keywords: `${BASE_KEYWORDS}, table games, multiplayer cards`,
  },

  arcade: {
    title: 'Arcade & Casual Games | KOB',
    description: 'Arcade and casual games at KOB—quick fun between slots, fishing and live casino sessions.',
    keywords: `${BASE_KEYWORDS}, arcade games online`,
  },

  promotion: {
    title: 'Casino Promotions & Bonuses | KOB',
    description:
      'Latest KOB promotions: welcome offers, reload bonuses and cashback for slots, live casino and card games. Check terms on each offer.',
    keywords: `${BASE_KEYWORDS}, casino bonus, welcome bonus, cashback`,
  },

  login: {
    title: 'Member Login | KOB',
    description: 'Log in to your KOB account to play slots, fishing, Shan Koe Mee, Bugyi, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, KOB login`,
  },

  register: {
    title: 'Register & Join | KOB',
    description:
      'Create a KOB account in minutes. Access slots, fishing games, Shan Koe Mee, Bugyi, live casino and secure banking.',
    keywords: `${BASE_KEYWORDS}, KOB register, sign up`,
  },

  contact: {
    title: 'Contact Us | KOB',
    description: 'Contact KOB support for account, deposits, withdrawals and game questions.',
    keywords: `${BASE_KEYWORDS}, KOB support, customer service`,
  },

  rule: {
    title: 'Rules & Terms | KOB',
    description: 'Rules, terms and responsible play information for KOB members.',
    keywords: `${BASE_KEYWORDS}, terms and conditions`,
  },

  deposit: {
    title: 'Deposit | KOB',
    description: 'Deposit funds securely to play slots, fishing, live casino and card games at KOB.',
    keywords: `${BASE_KEYWORDS}, deposit`,
    robots: 'noindex, nofollow',
  },

  withdraw: {
    title: 'Withdraw | KOB',
    description: 'Withdraw your winnings from KOB with supported payment methods.',
    keywords: `${BASE_KEYWORDS}, withdraw`,
    robots: 'noindex, nofollow',
  },

  banking: {
    title: 'Banking | KOB',
    description: 'Banking and payment options for KOB members.',
    keywords: `${BASE_KEYWORDS}, banking`,
    robots: 'noindex, nofollow',
  },

  history: {
    title: 'Transaction History | KOB',
    description: 'View your KOB transaction history.',
    robots: 'noindex, nofollow',
  },

  setting: {
    title: 'Account Settings | KOB',
    description: 'Manage your KOB account settings.',
    robots: 'noindex, nofollow',
  },

  betslip: {
    title: 'Betslip History | KOB',
    description: 'Your betslip history at KOB.',
    robots: 'noindex, nofollow',
  },

  liveScore: {
    title: 'Live Score | KOB',
    description: 'Live scores and sports updates at KOB.',
    keywords: BASE_KEYWORDS,
  },

  matchResult: {
    title: 'Football Match Results | KOB',
    description: 'View today and yesterday football match results at KOB.',
    keywords: `${BASE_KEYWORDS}, football results, match results`,
  },

  maungFb: {
    title: 'Maung FB | KOB',
    description: 'Maung FB at KOB.',
    keywords: BASE_KEYWORDS,
  },

  bodyFb: {
    title: 'Body FB | KOB',
    description: 'Body FB at KOB.',
    keywords: BASE_KEYWORDS,
  },

  videoLinks: {
    title: 'Video Links | KOB',
    description: 'Video links and guides at KOB.',
    keywords: BASE_KEYWORDS,
  },

  commission: {
    title: 'Commission | KOB',
    description: 'Commission program at KOB.',
    robots: 'noindex, nofollow',
  },

  level: {
    title: 'VIP Levels | KOB',
    description: 'VIP levels and rewards at KOB.',
    keywords: `${BASE_KEYWORDS}, VIP`,
  },

  guides: {
    title: 'App Install Guide | KOB',
    description: 'Install the KOB app on Android or iOS for slots, fishing, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, KOB app, install guide`,
  },

  androidGuide: {
    title: 'Android App Install | KOB — Slots, Fishing & Live Casino',
    description:
      'Install KOB on Android: play online slots, fishing games, Shan Koe Mee, Bugyi and live casino on your phone.',
    keywords: `${BASE_KEYWORDS}, KOB Android APK`,
  },

  iosGuide: {
    title: 'iOS App Install | KOB — Slots, Fishing & Live Casino',
    description:
      'Install KOB on iPhone or iPad: slots, fishing arcade, card games and live dealer casino.',
    keywords: `${BASE_KEYWORDS}, KOB iOS app`,
  },

  dreamBooks: {
    title: 'Dream Books | KOB',
    description: 'Dream books feature at KOB.',
    keywords: BASE_KEYWORDS,
  },

  twoD: {
    title: '2D | KOB',
    description: '2D games at KOB.',
    keywords: BASE_KEYWORDS,
  },

  threeD: {
    title: '3D | KOB',
    description: '3D games at KOB.',
    keywords: BASE_KEYWORDS,
  },

  landing: {
    title: 'KOB — Slots, Fishing, Live Casino & Card Games',
    description: BASE_DESC,
    keywords: BASE_KEYWORDS,
  },

  iframe: {
    title: 'Game | KOB',
    robots: 'noindex, nofollow',
  },

  verify: {
    title: 'Verify Account | KOB',
    robots: 'noindex, nofollow',
  },

  dataTable: {
    title: 'Data | KOB',
    robots: 'noindex, nofollow',
  },
};
